import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@utils/formatPrice';
import { isPostpaidTier, TIER_NAMES } from '@utils/productTier';
import type { ProductTier } from '../../types/shared';
import type { ContractTerm, Plan } from './types';

interface PlanSummaryProps {
  tier: ProductTier;
  contractTerm: ContractTerm;
  plan: Plan | undefined;
  isLoading: boolean;
}

// Package price of the selected tier and term as quoted by the server (net prices).
export const PlanSummary: React.FC<PlanSummaryProps> = ({
  tier,
  contractTerm,
  plan,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();

  if (!plan) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="plan-summary">
        {isLoading ? t('auth.planLoading') : t('auth.planUnavailable')}
      </p>
    );
  }

  const postpaid = isPostpaidTier(tier);
  const term =
    plan.terms.find(item => item.contract_type === (postpaid ? contractTerm : 'package_1')) ??
    plan.terms[0];
  const price = (value: string) => formatPrice(value, i18n.language, plan.currency);
  const number = (value: number) => new Intl.NumberFormat(i18n.language).format(value);
  const discount = Number(term.discount_percent);

  const rows: [string, string][] = postpaid
    ? [
        [
          t('auth.planIncludedMinutes'),
          t('auth.planPerMonthValue', { value: number(plan.included_minutes_monthly) }),
        ],
        [t('auth.planExtraMinute'), price(plan.minute_price)],
        [t('auth.planIncludedNumbers'), number(plan.included_phone_numbers)],
      ]
    : [
        [t('auth.planMinutePrice'), price(plan.minute_price)],
        [
          t('auth.planPhoneNumber'),
          t('auth.planPerMonthValue', { value: price(plan.phone_number_monthly) }),
        ],
      ];
  rows.push([
    t('auth.planAgents'),
    plan.max_agents === null ? t('auth.planUnlimited') : number(plan.max_agents),
  ]);

  return (
    <div
      className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-4 space-y-3"
      data-testid="plan-summary"
    >
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-semibold text-gray-900 dark:text-white">
          {TIER_NAMES[tier]}
          {postpaid && ` · ${t(`billing.contractType.${term.contract_type}`)}`}
        </span>
        {postpaid ? (
          <span className="text-right whitespace-nowrap">
            <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
              {price(term.agreed_monthly_fee)}
            </span>{' '}
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.planPerMonth')}
            </span>
          </span>
        ) : (
          <span className="font-semibold text-primary-600 dark:text-primary-400">
            {t('auth.planNoBaseFee')}
          </span>
        )}
      </div>

      {discount > 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('auth.planDiscount', {
            price: price(term.list_monthly_fee),
            discount: number(discount),
          })}
        </p>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-gray-600 dark:text-gray-400">{label}</dt>
            <dd className="text-right font-medium text-gray-900 dark:text-white">{value}</dd>
          </React.Fragment>
        ))}
      </dl>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {postpaid ? t('auth.planNotePostpaid') : t('auth.planNotePrepaid')}
      </p>
    </div>
  );
};
